import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { AutoAwesome as AutoAwesomeIcon } from "@mui/icons-material";
import { FaImage } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { ActionButton, FormTextField } from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import TemplateListPage, { statusParam } from "../../../components/organisms/TemplateListPage";
import TemplateEditorDrawer from "../../../components/organisms/TemplateEditorDrawer";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { fullDateTime, relativePast } from "../../Secretary/PatientsPage/patientListFormat";
import {
  useCreateDoctorImagingTemplateMutation,
  useListDoctorImagingTemplatesMutation,
  useSetDoctorImagingTemplateStatusMutation,
  useUpdateDoctorImagingTemplateMutation,
} from "../../../store/doctorApi";

export default function DoctorImagingTemplates() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", labels: [] });
  const [newLabel, setNewLabel] = useState("");
  const bootRef = useRef(false);

  const [listDoctorImagingTemplates] = useListDoctorImagingTemplatesMutation();
  const [createDoctorImagingTemplate, { isLoading: isCreateLoading }] =
    useCreateDoctorImagingTemplateMutation();
  const [updateDoctorImagingTemplate, { isLoading: isUpdateLoading }] =
    useUpdateDoctorImagingTemplateMutation();
  const [setDoctorImagingTemplateStatus] = useSetDoctorImagingTemplateStatusMutation();

  const isSaving = isCreateLoading || isUpdateLoading;

  const fetchList = async () => {
    if (!token) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await listDoctorImagingTemplates({
        token,
        query,
        status: statusParam(status),
        page: page + 1,
        limit: rowsPerPage,
      }).unwrap();
      setItems(res?.data?.templates || []);
      setTotal(res?.data?.pagination?.total || 0);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Şablonlar alınamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, rowsPerPage]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", labels: [] });
    setNewLabel("");
    setOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      name: template.name || "",
      labels: (template.items || []).map((i) => i.label || "").filter(Boolean),
    });
    setNewLabel("");
    setOpen(true);
  };

  const addLabel = () => {
    const t = String(newLabel || "").trim();
    if (!t) return;
    setForm((p) => ({ ...p, labels: [...p.labels, t] }));
    setNewLabel("");
  };

  const removeLabel = (index) => {
    setForm((p) => ({ ...p, labels: p.labels.filter((_, i) => i !== index) }));
  };

  const autoGenerate = () => {
    const allLabels = [
      "Akciğer Grafisi", "Kranial MR", "Abdomen USG", "Ekokardiyografi",
      "Servikal MR", "Lomber MR", "Toraks BT", "Abdomen BT",
      "Pelvik USG", "Tiroid USG", "Meme USG", "Panoramik Röntgen",
      "El Bilek Grafisi", "Kalça Grafisi", "Doppler USG",
    ];
    const count = Math.floor(Math.random() * 4) + 2;
    const selected = [...allLabels].sort(() => Math.random() - 0.5).slice(0, count);
    setForm({
      name: `Test Görüntüleme Şablonu ${Math.floor(Math.random() * 900) + 100}`,
      labels: selected,
    });
  };

  const onSave = async () => {
    if (!token || !form.name.trim()) return;
    const itemsPayload = form.labels
      .map((label) => ({ label: String(label).trim() }))
      .filter((i) => i.label);
    try {
      if (editing) {
        await updateDoctorImagingTemplate({
          token,
          templateId: editing._id,
          name: form.name,
          items: itemsPayload,
        }).unwrap();
        showSuccess("Şablon başarıyla güncellendi");
      } else {
        await createDoctorImagingTemplate({
          token,
          name: form.name,
          items: itemsPayload,
        }).unwrap();
        showSuccess("Şablon başarıyla oluşturuldu");
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Kaydetme işlemi başarısız.");
    }
  };

  const [togglingStatus, setTogglingStatus] = useState(false);
  const onToggleStatus = async (template) => {
    if (!token) return;
    setTogglingStatus(true);
    try {
      await setDoctorImagingTemplateStatus({
        token,
        templateId: template._id,
        isActive: !template.isActive,
      }).unwrap();
      showSuccess(template.isActive ? "Şablon pasife alındı" : "Şablon aktifleştirildi");
      setEditing((prev) => (prev && prev._id === template._id ? { ...prev, isActive: !template.isActive } : prev));
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Durum güncellenemedi.");
    } finally {
      setTogglingStatus(false);
    }
  };

  const canSave = useMemo(() => Boolean(form.name.trim()), [form.name]);

  const subText = { fontSize: TYPO.caption, color: COLORS.slate500 };
  const itemNames = (row) => (row.items || []).map((i) => i?.label).filter(Boolean);

  return (
    <TemplateListPage
      icon={<FaImage size={20} />}
      title="Görüntüleme Şablonları"
      subtitle="Sık istenen görüntüleme tetkiklerini tek tıkla eklemek için şablonlar"
      query={query}
      onQueryChange={(v) => {
        setPage(0);
        setQuery(v);
      }}
      status={status}
      onStatusChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      onCreate={openCreate}
      errorMsg={errorMsg}
      isLoading={isLoading}
      columns={[
        { key: "name", label: "Şablon", width: "34%" },
        { key: "items", label: "Tetkikler", width: "38%" },
        { key: "updated", label: "Güncellendi", width: "14%" },
        { key: "status", label: "Durum", width: "14%" },
      ]}
      rows={items}
      onRowClick={openEdit}
      renderRowCells={(row) => {
        const names = itemNames(row);
        const updated = row.updatedAt || row.createdAt;
        return [
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
            {row.name}
          </Typography>,
          names.length ? (
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} noWrap>
                {names.slice(0, 3).join(", ")}
              </Typography>
              <Typography sx={subText}>
                {names.length} tetkik{names.length > 3 ? ` · +${names.length - 3} daha` : ""}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          updated ? (
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} title={fullDateTime(updated)}>
              {relativePast(updated)}
            </Typography>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          <StatusPill label={row.isActive ? "Aktif" : "Pasif"} tone={row.isActive ? "success" : "neutral"} />,
        ];
      }}
      emptyIcon={<FaImage size={22} />}
      emptyMessage="Sık istediğiniz görüntüleme tetkiklerini şablon olarak kaydedin."
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    >
      <TemplateEditorDrawer
        open={open}
        onClose={() => setOpen(false)}
        icon={<FaImage size={18} />}
        title={editing ? "Şablonu düzenle" : "Yeni görüntüleme şablonu"}
        subtitle={editing ? editing.name : "Görüntüleme tetkiklerini şablon olarak kaydedin"}
        onSubmit={onSave}
        submitLabel={editing ? "Güncelle" : "Kaydet"}
        submitDisabled={!canSave}
        loading={isSaving}
        statusControl={
          editing
            ? { isActive: Boolean(editing.isActive), onToggle: () => onToggleStatus(editing), loading: togglingStatus }
            : null
        }
        headerExtra={
          !editing ? (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={autoGenerate}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: COLORS.warning,
                color: COLORS.warningDark,
                "&:hover": { bgcolor: COLORS.warningLightest, borderColor: COLORS.warningDark },
              }}
            >
              Otomatik doldur (test)
            </Button>
          ) : null
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Şablon adı *">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ör. Bel ağrısı görüntüleme"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <DrawerField label="Görüntüleme tetkikleri" hint="Yazıp Enter'a basın veya Ekle'ye tıklayın.">
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
              <FormTextField
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                placeholder="Ör. Akciğer grafisi, Lomber MR"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLabel())}
                InputProps={tileInputProps(true)}
                sx={{ flex: 1 }}
              />
              <ActionButton variant="add" palette={palette} onClick={addLabel} compact />
            </Box>
          </DrawerField>
          {form.labels.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {form.labels.map((label, i) => (
                <Chip
                  key={`${label}-${i}`}
                  label={label}
                  size="small"
                  onDelete={() => removeLabel(i)}
                  sx={{ fontWeight: 500, bgcolor: COLORS.slate100, color: COLORS.slate700 }}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>
              Henüz tetkik eklenmedi.
            </Typography>
          )}
        </Box>
      </TemplateEditorDrawer>
    </TemplateListPage>
  );
}
